import React, { useMemo, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { CheckCircle, Loader2, MessageCircle, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { IntlPhoneField } from '@/components/phone/IntlPhoneField';
import { cn } from '@/lib/utils';
import { defaultCountryFromLanguage } from '@/lib/contactForm/contactFormCopy';
import { buildAmbassadorPayload, buildAmbassadorWhatsappMessage, sanitizeAmbassadorDraft } from '@/lib/ambassadors/buildAmbassadorPayload';
import { submitContactPayload } from '@/lib/contactForm/submitContact';
import { getWsjCommercialWhatsappUrl } from '@/lib/wsjCommercialWhatsapp';

const labelClass = 'mb-2 block text-[0.8125rem] font-medium tracking-[0.06em] text-white/52';
const fieldBase =
  'rounded-xl border border-white/[0.09] bg-white/[0.035] text-base sm:text-[15px] text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] backdrop-blur-md transition-all duration-200 placeholder:text-white/26 placeholder:font-light placeholder:tracking-wide hover:border-white/[0.13] hover:bg-white/[0.05] focus-visible:border-amber-500/45 focus-visible:ring-2 focus-visible:ring-amber-400/18 focus-visible:ring-offset-0 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-45';
const fieldErrorClass =
  'border-red-400/40 !ring-2 !ring-red-500/20 focus-visible:!border-red-400/55 focus-visible:!ring-red-400/25';
const cardFrame =
  'relative rounded-[1.35rem] bg-gradient-to-br from-white/[0.14] via-white/[0.06] to-white/[0.02] p-px shadow-[0_28px_90px_-32px_rgba(0,0,0,0.72)]';
const cardInner = 'relative overflow-hidden rounded-[1.32rem] bg-slate-950/55 backdrop-blur-2xl p-6 sm:p-8';

export function AmbassadorsSignupForm({ copy, language, whatsappMessage }) {
  const defaultCountry = useMemo(() => defaultCountryFromLanguage(language), [language]);
  const [status, setStatus] = useState('idle');
  const [failedValues, setFailedValues] = useState(null);

  const schema = useMemo(
    () =>
      z.object({
        fullName: z.string().min(2, copy.formRequired).max(120),
        email: z.string().email(copy.formEmailInvalid).max(254),
        whatsapp: z
          .string()
          .min(1, copy.formRequired)
          .refine((v) => isValidPhoneNumber(v), copy.formPhoneInvalid),
        country: z.string().min(2, copy.formRequired).max(80),
        city: z.string().min(2, copy.formRequired).max(80),
        referralMethod: z.string().min(10, copy.formMessageMin).max(2000),
        paymentDetails: z.string().min(3, copy.formRequired).max(200),
        termsAccepted: z.literal(true, { errorMap: () => ({ message: copy.formTermsRequired }) })
      }),
    [copy]
  );

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting }
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      fullName: '',
      email: '',
      whatsapp: '',
      country: '',
      city: '',
      referralMethod: '',
      paymentDetails: '',
      termsAccepted: false
    }
  });

  const openWhatsapp = (values) => {
    const text = values ? buildAmbassadorWhatsappMessage(values) : whatsappMessage;
    window.open(getWsjCommercialWhatsappUrl(text), '_blank', 'noopener,noreferrer');
  };

  const onSubmit = async (raw) => {
    setStatus('idle');
    setFailedValues(null);
    const values = sanitizeAmbassadorDraft(raw);
    try {
      const payload = buildAmbassadorPayload(values, language);
      await submitContactPayload(payload);
      setStatus('success');
      reset();
    } catch {
      setFailedValues(values);
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <div className={cardFrame}>
        <div className={cn(cardInner, 'text-center py-10 sm:py-12')}>
          <CheckCircle className="w-12 h-12 text-emerald-400 mx-auto mb-4" aria-hidden />
          <h3 className="text-xl font-bold text-white mb-2">{copy.formSuccessTitle}</h3>
          <p className="text-sm text-white/50 font-light max-w-md mx-auto">{copy.formSuccessSubtitle}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={cardFrame}>
      <div className={cardInner}>
        {status === 'error' ? (
          <div className="mb-6 rounded-xl border border-red-500/25 bg-red-500/10 px-4 py-3 text-sm text-red-100/90">
            <p className="font-semibold">{copy.formErrorTitle}</p>
            <p className="text-red-100/70 font-light mt-1">{copy.formErrorSubtitle}</p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="mt-3 rounded-lg border-emerald-500/35 bg-emerald-500/10 text-emerald-100 hover:bg-emerald-500/15"
              onClick={() => openWhatsapp(failedValues)}
            >
              <MessageCircle className="w-4 h-4 mr-2" aria-hidden />
              {copy.formWhatsappFallback}
            </Button>
          </div>
        ) : null}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="sm:col-span-2">
              <Label htmlFor="amb-fullName" className={labelClass}>
                {copy.formFullName}
              </Label>
              <Input
                id="amb-fullName"
                autoComplete="name"
                placeholder={copy.formFullNamePlaceholder}
                className={cn(fieldBase, 'h-11', errors.fullName && fieldErrorClass)}
                {...register('fullName')}
              />
              {errors.fullName ? <p className="mt-1.5 text-xs text-red-300/90">{errors.fullName.message}</p> : null}
            </div>

            <div>
              <Label htmlFor="amb-email" className={labelClass}>
                {copy.formEmail}
              </Label>
              <Input
                id="amb-email"
                type="email"
                autoComplete="email"
                placeholder={copy.formEmailPlaceholder}
                className={cn(fieldBase, 'h-11', errors.email && fieldErrorClass)}
                {...register('email')}
              />
              {errors.email ? <p className="mt-1.5 text-xs text-red-300/90">{errors.email.message}</p> : null}
            </div>

            <div>
              <Label htmlFor="amb-whatsapp" className={labelClass}>
                {copy.formWhatsapp}
              </Label>
              <Controller
                name="whatsapp"
                control={control}
                render={({ field }) => (
                  <IntlPhoneField
                    id="amb-whatsapp"
                    value={field.value}
                    onChange={field.onChange}
                    defaultCountry={defaultCountry}
                    className={cn(errors.whatsapp && fieldErrorClass)}
                  />
                )}
              />
              {errors.whatsapp ? <p className="mt-1.5 text-xs text-red-300/90">{errors.whatsapp.message}</p> : null}
            </div>

            <div>
              <Label htmlFor="amb-country" className={labelClass}>
                {copy.formCountry}
              </Label>
              <Input
                id="amb-country"
                autoComplete="country-name"
                placeholder={copy.formCountryPlaceholder}
                className={cn(fieldBase, 'h-11', errors.country && fieldErrorClass)}
                {...register('country')}
              />
              {errors.country ? <p className="mt-1.5 text-xs text-red-300/90">{errors.country.message}</p> : null}
            </div>

            <div>
              <Label htmlFor="amb-city" className={labelClass}>
                {copy.formCity}
              </Label>
              <Input
                id="amb-city"
                autoComplete="address-level2"
                placeholder={copy.formCityPlaceholder}
                className={cn(fieldBase, 'h-11', errors.city && fieldErrorClass)}
                {...register('city')}
              />
              {errors.city ? <p className="mt-1.5 text-xs text-red-300/90">{errors.city.message}</p> : null}
            </div>

            <div className="sm:col-span-2">
              <Label htmlFor="amb-referral" className={labelClass}>
                {copy.formReferralMethod}
              </Label>
              <Textarea
                id="amb-referral"
                rows={3}
                placeholder={copy.formReferralMethodPlaceholder}
                className={cn(fieldBase, 'min-h-[96px] resize-y', errors.referralMethod && fieldErrorClass)}
                {...register('referralMethod')}
              />
              {errors.referralMethod ? (
                <p className="mt-1.5 text-xs text-red-300/90">{errors.referralMethod.message}</p>
              ) : null}
            </div>

            <div className="sm:col-span-2">
              <Label htmlFor="amb-payment" className={labelClass}>
                {copy.formPaymentDetails}
              </Label>
              <Input
                id="amb-payment"
                placeholder={copy.formPaymentDetailsPlaceholder}
                className={cn(fieldBase, 'h-11', errors.paymentDetails && fieldErrorClass)}
                {...register('paymentDetails')}
              />
              {errors.paymentDetails ? (
                <p className="mt-1.5 text-xs text-red-300/90">{errors.paymentDetails.message}</p>
              ) : null}
            </div>

            <div className="sm:col-span-2">
              <Controller
                name="termsAccepted"
                control={control}
                render={({ field }) => (
                  <label className="flex items-start gap-3 cursor-pointer group">
                    <Checkbox
                      checked={field.value === true}
                      onCheckedChange={(v) => field.onChange(v === true)}
                      className="mt-0.5 border-white/20 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500"
                    />
                    <span className="text-sm text-white/55 font-light leading-relaxed group-hover:text-white/70 transition-colors">
                      {copy.formTermsLabel}
                    </span>
                  </label>
                )}
              />
              {errors.termsAccepted ? (
                <p className="mt-1.5 text-xs text-red-300/90">{errors.termsAccepted.message}</p>
              ) : null}
            </div>
          </div>

          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full sm:w-auto rounded-xl h-12 px-8 bg-gradient-to-r from-amber-500 to-amber-600 text-slate-950 font-semibold shadow-lg shadow-amber-900/25 hover:from-amber-400 hover:to-amber-500"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" aria-hidden />
                {copy.formSubmitting}
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" aria-hidden />
                {copy.formSubmit}
              </>
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}
